import React from 'react';
import BonusPointsCard from '~/shared/components/BonusPointsCard/BonusPointsCard';
import styles from './BonusPointsList.module.scss';
import { useTranslation } from '~/shared/utils/translation';
import Typography from '~/shared/components/Typography/Typography';
import { Anchor } from '~/shared/components/Anchor/Anchor';
import Spinner from '~/shared/components/Spinner/Spinner';

export interface BonusReward {
    id: string;
    amount: number;
    currency?: string;
    expiryDate: string;
}

interface BonusPointsListProps {
    rewards: BonusReward[];
    className?: string;
    isLoading?: boolean;
    hasError?: boolean;
}

const BonusPointsList: React.FC<BonusPointsListProps> = ({
    rewards,
    className,
    isLoading,
    hasError,
}) => {
    const { translate } = useTranslation();

    const hasRewards = !hasError && rewards && rewards.length > 0;

    // Use different titles based on loading state and whether there are rewards
    const titleKey = isLoading
        ? 'loyalty.bonusPoints.title.loading'
        : hasRewards
          ? 'loyalty.bonusPoints.title'
          : 'loyalty.bonusPoints.title.noEarnings';

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <div className={styles.titleContainer}>
                <Typography variant="title-S" as="h2">
                    {translate(titleKey)}
                </Typography>
                {isLoading && <Spinner className={styles.titleSpinner} />}
            </div>
            <div className={styles.grid}>
                {hasRewards ? (
                    rewards.map((reward) => (
                        <div key={reward.id} className={styles.cardWrapper}>
                            <BonusPointsCard
                                amount={reward.amount}
                                currency={reward.currency}
                                expiryDate={reward.expiryDate}
                            />
                        </div>
                    ))
                ) : (
                    <div className={styles.cardWrapper}>
                        <BonusPointsCard variant="empty" />
                    </div>
                )}
            </div>
            <footer className={styles.footer}>
                <Anchor href="/">{translate('loyalty.bonusPoints.link.about')}</Anchor>
            </footer>
        </div>
    );
};

export default BonusPointsList;
